import { format, parse } from 'date-fns-modern';
import { tz } from '@date-fns/tz';
import uniq from 'lodash/uniq';
import uniqBy from 'lodash/uniqBy';
import {
  Fare,
  getSchedules,
  ReservationDetails,
  ScheduleProduct,
  ScheduleRun,
  Schedules,
  TDSScheduleRequestBody
} from '@/apis/tds';
import {
  getStopBySlug,
  getCityBySlug,
  getStops,
  StrapiCarrier,
  StrapiStop,
  StrapiCityDatum,
  StrapiCountries,
  StrapiCityRouteDatum,
  StrapiMediaDatum
} from '@/apis/strapi';
import { ResponsiveImageMap } from '@/utils/types';
import logger from '@/logger';
import { WebhookConfig } from '@/utils/webhook';

export const getCarrier = (schedule: ScheduleProduct, carrierInfo?: { [key: string]: StrapiCarrier }) => {
  if (!schedule.segments) {
    return null;
  }
  const carriers = schedule.segments.map((segment: any) => {
    return { code: segment.schedule.carrier.code.trim(), name: segment.schedule.carrier.name.trim() };
  });
  const finalCarriers = carrierInfo ? groupCarriers(carriers, carrierInfo) : carriers;
  if (finalCarriers.length) {
    return finalCarriers[0];
  }
};

export const getCarriers = (schedule: ScheduleProduct, carrierInfo?: { [key: string]: StrapiCarrier }) => {
  if (!schedule.segments) {
    return [];
  }
  const carriers = schedule.segments.map((segment: any) => {
    return { code: segment.schedule.carrier.code.trim(), name: segment.schedule.carrier.name.trim() };
  });
  const finalCarriers = carrierInfo ? groupCarriers(carriers, carrierInfo) : carriers;
  if (finalCarriers.every((value: any) => value.code === finalCarriers[0].code)) {
    return [finalCarriers[0]];
  } else if (
    carrierInfo &&
    finalCarriers.every((value: any) => {
      if (!carrierInfo[value.code]) {
        logger.log('error', `Unknown carrier '${value.code}' found`);
        return false;
      }
      return carrierInfo[value.code]?.attributes?.trailways_carrier;
    })
  ) {
    return [finalCarriers[0]];
  }
  return uniqBy(finalCarriers, 'code');
};

const groupCarriers = (carriers: { code: string; name: string }[], carrierInfo: { [key: string]: StrapiCarrier }) => {
  return carriers.map((carrier: { code: string; name: string }) => {
    if (carrierInfo && carrierInfo[carrier.code] && carrierInfo[carrier.code].attributes.group_with_carrier.data) {
      const linkedCarrierName = carrierInfo[carrier.code].attributes.group_with_carrier.data.attributes?.carrier_name;
      const linkedCarrierSlug = carrierInfo[carrier.code].attributes.group_with_carrier.data.attributes?.carrier_slug;
      const linkedCarrierCode =
        Object.values(carrierInfo).find((c) => c.attributes.carrier_slug === linkedCarrierSlug)?.attributes
          .carrier_id || null;
      if (linkedCarrierCode) {
        return {
          code: linkedCarrierCode,
          name: linkedCarrierName
        };
      }
    }
    return carrier;
  });
};

export const getOriginAndDestinationSlugsFromTripSlug = (trip_slug: string) => {
  interface originAndDestinationSlugs {
    originSlug: string | null;
    destinationSlug: string | null;
  }
  const originAndDestinationSlugs: originAndDestinationSlugs = {
    originSlug: null,
    destinationSlug: null
  };
  trip_slug = trip_slug.toLowerCase();
  const trip_slug_regex = /bus-from-([a-z0-9\-]+?)-to-([a-z0-9\-]+?)\/?$/;
  const match_result = trip_slug.match(trip_slug_regex);
  if (match_result && match_result.length === 3) {
    //console.log("Locations parsed from URL slug: " + match_result[1] + ", " + match_result[2]);
    (originAndDestinationSlugs.originSlug = match_result[1]),
      (originAndDestinationSlugs.destinationSlug = match_result[2]);
    return originAndDestinationSlugs;
  }
  const trip_slug_regex2 = /bus-from-([a-z0-9\-]+?)\/?$/;
  const match_result2 = trip_slug.match(trip_slug_regex2);
  if (match_result2 && match_result2.length === 2) {
    originAndDestinationSlugs.originSlug = match_result2[1];
  }
  return originAndDestinationSlugs;
};

// Given a location slug, get all the info for its city, including other stops in the city
export async function getCityAndStopsFromSlug(locationSlug: string, includeStopDetails: boolean) {
  const locationCityData = await getCityBySlug(locationSlug, includeStopDetails);
  // If it's a city
  if (locationCityData) {
    return {
      city: locationCityData,
      citySlug: locationSlug,
      cityStops: locationCityData.attributes.stops?.data,
      nearbyStops: locationCityData.attributes.nearby_stops?.data,
      requestedSlug: locationSlug
    };
  } else {
    // If it's not a city
    // See if it's a stop
    const locationStop = await getStopBySlug(locationSlug);

    // If it is a stop, get the stop's city, so we can redirect to it later
    // (City isn't guaranteed to be there, so make sure to check for it explicitly)
    if (
      locationStop &&
      locationStop.attributes &&
      locationStop.attributes.cities &&
      locationStop.attributes.cities.data &&
      locationStop.attributes.cities.data.length
    ) {
      return {
        city: null,
        citySlug: locationStop.attributes.cities.data[0].attributes.city_slug,
        cityStops: null,
        nearbyStops: null,
        requestedSlug: locationSlug
      };
    }
  }
  return null;
}

// Given a location slug, get all the info for its city, including other stops in the city
export async function getCityAndBasicStopsFromSlug(locationSlug: string) {
  return await getCityAndStopsFromSlug(locationSlug, false);
}
export async function getCityAndFullStopsFromSlug(locationSlug: string) {
  return await getCityAndStopsFromSlug(locationSlug, true);
}

// Given a trip slug (e.g. 'bus-from-city1-to-city2'), get all the strapi info for the origin
// and the destination.  This is in it's own function so that the generateMetaData and
// page body can minimize duplicated code
export const getOriginAndDestinationFromTripSlug = async (trip_slug: string) => {
  interface originAndDestination {
    originCityData: StrapiCityDatum | null;
    originStops: StrapiStop[] | null;
    originCitySlug: string;
    destinationCityData: StrapiCityDatum | null;
    destinationStops: StrapiStop[] | null;
    destinationCitySlug: string;
  }
  const originAndDestination: originAndDestination = {
    originCityData: null,
    originStops: null,
    originCitySlug: '',
    destinationCityData: null,
    destinationStops: null,
    destinationCitySlug: ''
  };

  const { originSlug, destinationSlug } = getOriginAndDestinationSlugsFromTripSlug(trip_slug);
  if (!originSlug && !destinationSlug) {
    return originAndDestination;
  }
  const promises = [];
  if (originSlug) {
    promises.push(getCityAndBasicStopsFromSlug(originSlug));
  }
  if (destinationSlug) {
    if (originSlug !== destinationSlug) {
      promises.push(getCityAndBasicStopsFromSlug(destinationSlug));
    }
  }
  await Promise.allSettled(promises).then((results) => {
    results.forEach((result) => {
      if (result.status === 'fulfilled') {
        const data = result.value;
        if (data) {
          if (data.requestedSlug === originSlug) {
            originAndDestination.originCitySlug = data.citySlug;
            if (data.city) {
              originAndDestination.originCityData = data.city;
            }
            if (data.cityStops) {
              originAndDestination.originStops = data.cityStops;
            }
          } else if (data.requestedSlug === destinationSlug) {
            originAndDestination.destinationCitySlug = data.citySlug;
            if (data.city) {
              originAndDestination.destinationCityData = data.city;
            }
            if (data.cityStops) {
              originAndDestination.destinationStops = data.cityStops;
            }
          }
        }
      }
    });
  });
  if (originSlug === destinationSlug) {
    originAndDestination.destinationCitySlug = originAndDestination.originCitySlug;
    originAndDestination.destinationCityData = originAndDestination.originCityData;
    originAndDestination.destinationStops = originAndDestination.originStops;
  }
  return originAndDestination;
};

export const isAdvancedFare = (fare: Fare) => {
  return fare.type.toLowerCase().includes('advanced');
};

export const isMileageFare = (fare: Fare) => {
  return fare.type.toLowerCase().includes('mileage');
};

export const getFares = (schedule: ScheduleProduct) => {
  const fares = getRankedFares(schedule) || [];
  const saver = fares[0];
  const flex = fares[1];
  return {
    flex: [flex],
    saver: [saver]
  };
};

// Determine the best markup and flex fares for a particular schedule
export const getRankedFares = (schedule: ScheduleProduct) => {
  if (!schedule.railgunFares || !schedule.railgunFares.Adult) {
    return;
  }
  const mileageFareDetails = {
    fareId: '',
    baseFare: 0,
    endorsement: undefined,
    fareAdjustments: [],
    fareCalcId: '',
    fareCalcName: '',
    fareCalcRuleId: '',
    markup: '',
    markupApplied: false,
    markupId: '',
    mileageFareRateId: '',
    mileageFareTable: '',
    mileageFareTableId: '',
    passengerType: '',
    surchargeId: undefined,
    tariff: '',
    tariffDescription: ''
  };
  // Typically, there's only one set of railgunFares, but let's iterate to be sure
  for (const fareSet of schedule.railgunFares.Adult) {
    if (!fareSet) {
      continue;
    }
    // For railgun fares, the rule is:
    // - Advanced Fares are better than Mileage fares
    // For the SEO pages, the rule is:
    // - Show non-markup fares when available as they're cheaper.
    //
    // The means the ranking is:
    //   1: Advanced / no-markup
    //   2: Advanced / markup
    //   3: Mileage / no-markup
    //   4: Mileage / markup
    //
    // So lets rank the fares to match this ruleset
    const rankedFares: (Fare | null)[] = [null, null, null, null];
    for (const fare of fareSet.fares) {
      if (!fare.amount) {
        continue;
      }
      const fareType = fare.type;
      const markup = fare.markup;

      const isAdvanced = isAdvancedFare(fare);
      const isMileage = isMileageFare(fare);
      // Best option
      if (isAdvanced && markup === false) {
        rankedFares[0] = fare;
        // Second-best option
      } else if (isAdvanced && markup === true) {
        rankedFares[1] = fare;
        // Third-best option
      } else if (isMileage && markup === false) {
        rankedFares[2] = fare;
        // Fourth-best option
      } else if (isMileage && markup === true) {
        rankedFares[3] = fare;
      }
    }
    // If trip has Advanced Fares, use them exclusively
    if (rankedFares[0] || rankedFares[1]) {
      return [rankedFares[0], rankedFares[1]];
    } else {
      return [rankedFares[2], rankedFares[3]];
    }
  }
};

export const getRescheduleFares = (
  schedule: ScheduleProduct,
  reservationDetails: ReservationDetails,
  rescheduleLeg: string
) => {
  const newFares: { flex: [Fare | null]; saver: [Fare | null] } = {
    flex: [null],
    saver: [null]
  };
  if (!schedule.railgunFares) {
    return newFares;
  }
  let passengerType = 'Adult';
  let railgunFares = schedule.railgunFares.Adult;
  if (!railgunFares && schedule.railgunFares.Senior) {
    railgunFares = schedule.railgunFares.Senior;
    passengerType = 'Senior';
  }
  const passenger = reservationDetails.passengers.find(
    (passenger: ReservationDetails['passengers'][0]) => passenger.type === passengerType
  );
  if (!passenger) {
    return newFares;
  }
  const originalFare = getRescheduleOriginalFare(
    rescheduleLeg === 'depart' ? passenger.outboundFare : passenger.returnFare
  );

  for (const fareSet of railgunFares) {
    if (!fareSet) {
      continue;
    }
    const advancedFares = fareSet.fares.filter((fare) => fare.amount && isAdvancedFare(fare));
    const mileageFares = fareSet.fares.filter((fare) => fare.amount && isMileageFare(fare));
    if (originalFare.type === 'Advanced Fare') {
      if (advancedFares.length) {
        newFares.saver = [advancedFares.find((fare) => fare.markup === false) || null];
        newFares.flex = [advancedFares.find((fare) => fare.markup === true) || null];
      } else if (mileageFares.length) {
        newFares.saver = [mileageFares.find((fare) => fare.markup === false) || null];
        newFares.flex = [mileageFares.find((fare) => fare.markup === true) || null];
      }
    }
    if (originalFare.type === 'Mileage Fare' && mileageFares.length) {
      newFares.saver = [mileageFares.find((fare) => fare.markup === false) || null];
      newFares.flex = [mileageFares.find((fare) => fare.markup === true) || null];
    }
  }

  return newFares;
};

export const getRescheduleOriginalFare = (fare: Fare) => {
  return {
    amount: fare.amount,
    markup: fare.isMarkup || false,
    type: fare.type,
    [fare.type === 'Advanced Fare' ? 'specialFareId' : 'mileageFareId']: fare.fareId
  };
};

// If we're only showing one price for a schedule, determine the one to show
export const determineWhichFareToShow = (schedule: ScheduleProduct) => {
  const rankedFares = getRankedFares(schedule);
  for (const fare of rankedFares || []) {
    if (fare) {
      return fare;
    }
  }
};

export const formatTime = (date: string, timeZone: string) => {
  return format(new Date(date), 'h:mm aaa', { in: tz(timeZone) });
};

export const formatPrice = (price: number | undefined) => {
  if (!price) {
    return 'N/A';
  }
  if (Number.isInteger(price)) {
    return `$${price}`;
  }
  return `$${price.toFixed(2)}`;
};

export const formatRoundedPrice = (price: number | undefined) => {
  if (!price) {
    return 'N/A';
  }
  return `$${Math.round(price)}`;
};

export const formatDate = (date: Date) => {
  return format(date, 'MM/dd/yyyy');
};

export const formatDateYearMonthDay = (date: string, timeZone: string) => {
  return format(new Date(date), 'yyyy-MM-dd', { in: tz(timeZone) });
};

export const formatDateString = (date: string, timeZone: string) => {
  return format(new Date(date), 'MM/dd/yyyy', { in: tz(timeZone) });
};

export const formatDepartOrReturnDate = (date: string, timeZone: string) => {
  return format(new Date(date), 'yyyy_MM_dd'), { in: tz(timeZone) };
};

export const formatLongDateString = (date: string) => {
  return format(new Date(date), 'LLLL dd, yyyy');
};

// Sort schedules
// Show departed trips and soldout last
export const sortUnavailableSchedulesToLast = (a: ScheduleProduct, b: ScheduleProduct) => {
  const aDeparted = a.scheduleRun.alreadyDeparted ? 1 : 0;
  const bDeparted = b.scheduleRun.alreadyDeparted ? 1 : 0;
  if (aDeparted != bDeparted) {
    return aDeparted - bDeparted;
  }
  const aSoldOut = a.scheduleRun.soldOut ? 1 : 0;
  const bSoldOut = b.scheduleRun.soldOut ? 1 : 0;
  if (aSoldOut != bSoldOut) {
    return aSoldOut - bSoldOut;
  }
  const aUnavailable = a.scheduleRun.isUnavailable ? 1 : 0;
  const bUnavailable = b.scheduleRun.isUnavailable ? 1 : 0;
  if (aUnavailable != bUnavailable) {
    return aUnavailable - bUnavailable;
  }
  return 0;
};

export const orderBy = (schedulesArray: ScheduleProduct[], column: string) => {
  switch (column) {
    case 'departure time':
      schedulesArray.sort((one, two) => {
        const dateOne = new Date(one.scheduleRun.departTime);
        const dateTwo = new Date(two.scheduleRun.departTime);
        return dateOne.getTime() - dateTwo.getTime();
      });
      break;
    case 'flex':
    case 'saver':
      schedulesArray.sort((one, two) => {
        const amountOne = getFares(one)[column][0]?.amount;
        const amountTwo = getFares(two)[column][0]?.amount;

        if (amountOne === undefined) {
          return 1;
        }
        if (amountTwo === undefined) {
          return -1;
        }

        return (amountOne || 0) - (amountTwo || 0);
      });
      break;
    case 'price':
      schedulesArray.sort((one, two) => {
        const amountOne = determineWhichFareToShow(one)?.amount || 0;
        const amountTwo = determineWhichFareToShow(two)?.amount || 0;
        return amountOne - amountTwo;
      });
      break;
    case 'duration':
      schedulesArray.sort((one, two) => one.scheduleRun.travelDuration - two.scheduleRun.travelDuration);
      break;
    case 'transfers':
      schedulesArray.sort((one, two) => {
        const transfersOne = one.segments.filter((segment: any) => segment.transfer).length;
        const transfersTwo = two.segments.filter((segment: any) => segment.transfer).length;
        return transfersOne - transfersTwo;
      });
      break;
  }
  return schedulesArray.sort(sortUnavailableSchedulesToLast);
};

// Determine if we will allow buying a ticket for a given schedule
export const isScheduleBookable = (scheduleRun: ScheduleRun) => {
  return (
    !scheduleRun.soldOut &&
    !scheduleRun.alreadyDeparted &&
    !scheduleRun.expired &&
    !scheduleRun.pastSalesCutoffTime &&
    !scheduleRun.isUnavailable
  );
};
export const getBackgroundImages = (strapiCityDatum: StrapiCityDatum | null) => {
  const backgroundImages: ResponsiveImageMap = {
    desktop: {},
    tablet: {},
    mobile: {}
  };

  // Decide to show a city or state photo.
  // If we have a city photo, show that one.  Otherwise, show a state photo if we have that.
  // Whichever we decide, we'll put it in this photoSet object
  const photoSet: {
    [device: string]: StrapiMediaDatum[];
  } = {};
  if (
    strapiCityDatum &&
    strapiCityDatum.attributes.hero_desktop &&
    strapiCityDatum.attributes.hero_desktop.data &&
    strapiCityDatum.attributes.hero_desktop.data.length &&
    strapiCityDatum.attributes.hero_desktop.data[0].attributes
  ) {
    //console.log("Found a city image");
    photoSet['desktop'] = strapiCityDatum.attributes.hero_desktop.data;
    photoSet['tablet'] = strapiCityDatum.attributes.hero_tablet?.data || photoSet['desktop'];
    photoSet['mobile'] = strapiCityDatum.attributes.hero_mobile?.data || photoSet['tablet'];
  } else if (
    strapiCityDatum &&
    strapiCityDatum.attributes.state &&
    strapiCityDatum.attributes.state.data.attributes.hero_desktop &&
    strapiCityDatum.attributes.state.data.attributes.hero_desktop.data &&
    strapiCityDatum.attributes.state.data.attributes.hero_desktop.data.length &&
    strapiCityDatum.attributes.state.data.attributes.hero_desktop.data[0].attributes
  ) {
    //console.log("Found a state image");
    photoSet['desktop'] = strapiCityDatum.attributes.state.data.attributes.hero_desktop.data;
    photoSet['tablet'] = strapiCityDatum.attributes.state.data.attributes.hero_tablet?.data || photoSet['desktop'];
    photoSet['mobile'] = strapiCityDatum.attributes.state.data.attributes.hero_mobile?.data || photoSet['tablet'];
  }
  // Now that we've decided which photo to show, handle the pixel density bits.
  ['desktop', 'tablet', 'mobile'].forEach((device) => {
    if (photoSet[device] && photoSet[device].length) {
      photoSet[device].forEach((photo) => {
        if (!photo.attributes) {
          return;
        }
        let density = '1x';
        if (photo.attributes.name.includes('2x')) {
          density = '2x';
        }
        //console.log("Photo: " + device + " " + density + ": " + photo.attributes.name);
        //console.log(photo.attributes.formats);
        backgroundImages[device][density] = photo.attributes.url;
      });
    }
  });
  return backgroundImages;
};

const getStopDetails = async (params: any) => {
  const stop = await getStops(params);
  if (stop && stop.data.length > 0) {
    return stop.data[0];
  }
  return null;
};

export const getStopAndCityData = async (city_or_stop_slug: string) => {
  return await getStopDetails({
    'filters[stop_enabled][$eq]': true,
    'filters[stop_slug][$eq][0]': city_or_stop_slug,
    'populate[cities][fields][0]': 'city_name',
    'populate[cities][fields][1]': 'city_slug',
    'populate[cities][populate][state][fields][0]': 'state_id',
    'populate[Hours]': 'true',
    'populate[location_hours]': 'true',
    'populate[amenities]': 'true',
    'populate[payment_forms]': 'true',
    'fields[0]': 'stop_boarding_instructions',
    'fields[1]': 'phone_primary',
    'fields[2]': 'phone_secondary',
    'fields[3]': 'phone_tertiary',
    'fields[4]': 'stop_address1',
    'fields[5]': 'stop_address2',
    'fields[6]': 'stop_advanced_notice',
    'fields[7]': 'stop_latitude',
    'fields[8]': 'stop_longitude',
    'fields[9]': 'stop_name',
    'fields[10]': 'stop_postal_code',
    'fields[11]': 'stop_show_open_24x7',
    'fields[12]': 'stop_slug',
    'fields[13]': 'stop_type',
    'fields[14]': 'stop_special_announcements',
    'filters[stop_type][$not][$eq]': 'Non-Passenger Stop',
    'populate[partner_location_keys][fields][0]': 'partner_primary_key'
  });
};

export const getTransfers = (segments: any) => {
  if (!segments) {
    return [];
  }
  const transfers: any = [];
  let isNewLeg = true;
  let legDepartDate = new Date();
  let legOrigin = '';
  segments.forEach((segment: any, index: number) => {
    if (index > 0 && segment.transfer) {
      const transfer = segments[index - 1];
      const legArriveDate = transfer.arriveTime;
      const travelDuration = (new Date(legArriveDate).getTime() - new Date(legDepartDate).getTime()) / 60000;
      transfer.departStop = legOrigin;
      transfer.departTime = legDepartDate;
      transfer.travelDuration = travelDuration;
      transfer.transferTo = segments[index].schedule.trueNumber;
      transfer.transferToFullName = `${segments[index].schedule.carrier.name} ${segments[index].schedule.scheduleNumber}`;
      transfer.layoverDuration = segments[index].layoverDuration;
      transfer.isTransferAvailable = true;
      transfers.push(transfer);
      isNewLeg = true;
    }
    if (isNewLeg) {
      legOrigin = segment.departStop;
      legDepartDate = segment.departTime;
      isNewLeg = false;
    }
    if (index + 1 === segments.length) {
      const transfer = segment;
      const legArriveDate = transfer.arriveTime;
      const travelDuration = (new Date(legArriveDate).getTime() - new Date(legDepartDate).getTime()) / 60000;
      transfer.departStop = legOrigin;
      transfer.departTime = legDepartDate;
      transfer.travelDuration = travelDuration;
      transfer.isTransferAvailable = false;
      transfers.push(transfer);
    }
  });
  return transfers;
};

export const getStopName = (cityData: StrapiCityDatum | null | undefined, stopUuid: string) => {
  if (!cityData || !cityData.attributes.stops) {
    return null;
  }
  const stop = cityData?.attributes.stops.data.find((stop) => {
    if (
      stop.attributes.partner_location_keys &&
      stop.attributes.partner_location_keys.data &&
      stop.attributes.partner_location_keys.data.length
    ) {
      return stop.attributes.partner_location_keys.data.some((key) => key.attributes.partner_primary_key === stopUuid);
    }
    return false;
  });
  return stop?.attributes?.stop_name;
};

const getCountry = (stateName: string, stateId: string, cityName: string, locations: StrapiCountries) => {
  const stateSearch = locations.data.find((country) =>
    country.attributes.states?.data.find(
      (state) => state.attributes.state_name === stateName || state.attributes.state_id === stateId
    )
  );
  if (stateSearch) {
    return stateSearch.attributes.country_id;
  }

  const citySearch = locations.data.find((country) =>
    country.attributes.states?.data.find((state) =>
      state.attributes.cities?.data.find((cityObj) => cityObj.attributes.city_name === cityName.split(',')[0].trim())
    )
  );
  if (citySearch) {
    return citySearch.attributes.country_id;
  }
  return null;
};

const getCountries = (schedule: ScheduleProduct, locations: StrapiCountries) => {
  return schedule.segments.flatMap((segment) => {
    const { arriveStop, departStop } = segment;
    const countries: string[] = [];

    const addCountry = (stop: ScheduleRun['origin']) => {
      const { state, city } = stop;
      if (state?.country && state.country !== 'TODO') {
        countries.push(state.country);
      } else {
        state.name = state.name ? state.name.trim() : '';
        state.abbreviation = state.abbreviation ? state.abbreviation.trim() : '';
        city.name = city.name ? city.name.trim() : '';
        const country = getCountry(state.name, state.abbreviation, city.name, locations);
        if (country) {
          countries.push(country);
        }
      }
    };

    addCountry(arriveStop);
    addCountry(departStop);

    return countries;
  });
};

export const isCrossing = (
  outboundSchedule: ScheduleProduct | undefined,
  returnSchedule: ScheduleProduct | undefined,
  locations: StrapiCountries
) => {
  if (outboundSchedule) {
    const outboundCountries = getCountries(outboundSchedule, locations);
    if (uniq(outboundCountries).length > 1) {
      return true;
    }
  }
  if (returnSchedule) {
    const returnCountries = getCountries(returnSchedule, locations);
    if (uniq(returnCountries).length > 1) {
      return true;
    }
  }
  return false;
};

function getScheduleData(
  originUUID: string,
  destinationUUID: string,
  isReturn: boolean,
  date: Date,
  timeZone: string,
  secondsToCache: number,
  userAgent: string
) {
  const params: TDSScheduleRequestBody = {
    departDate: format(date, 'yyyy-MM-dd', { in: tz(timeZone) }),
    destination: { stopUuid: destinationUUID },
    origin: { stopUuid: originUUID },
    passengerCounts: {
      Adult: 1,
      Military: 0,
      Senior: 0,
      Child: 0,
      Student: 0
    },
    isReturn: isReturn,
    purchaseType: 'SCHEDULE_BOOK'
  };
  const webhookConfig: WebhookConfig = {
    userAgent: userAgent
  };
  try {
    return getSchedules(params, secondsToCache, webhookConfig);
  } catch (error) {
    return null;
  }
}

export const generateSchedDataCalls = (
  originStops: StrapiStop[] | null,
  destinationStops: StrapiStop[] | null,
  isReturn: boolean,
  travelDate: Date,
  timeZone: string,
  cacheSeconds: number,
  webhookUserAgent: string
) => {
  let schedDataCalls: Promise<Schedules | null | undefined>[] = [];
  if (originStops && destinationStops) {
    for (const originStop of originStops) {
      for (const destinationStop of destinationStops) {
        if (
          originStop.attributes.partner_location_keys &&
          originStop.attributes.partner_location_keys.data.length &&
          destinationStop.attributes.partner_location_keys &&
          destinationStop.attributes.partner_location_keys.data.length
        ) {
          for (const originKey of originStop.attributes.partner_location_keys.data) {
            for (const destinationKey of destinationStop.attributes.partner_location_keys.data) {
              // Get a promise for a TDS schedule call for an origin/destination pair
              const thisScheduleCall = getScheduleData(
                originKey.attributes.partner_primary_key,
                destinationKey.attributes.partner_primary_key,
                isReturn,
                travelDate,
                timeZone,
                cacheSeconds,
                webhookUserAgent
              );
              // Add it to the list of promises
              if (thisScheduleCall) {
                schedDataCalls.push(thisScheduleCall);
              }
            }
          }
        } else {
          const errorText = `One or both stops are missing UUIDs: '${originStop.attributes.stop_slug}' '${destinationStop.attributes.stop_slug}'`;
          logger.log('warn', errorText);
        }
      }
    }
  }
  return schedDataCalls;
};

export const formatTripSearchDate = (date: string) => {
  const dateObject = parse(date, 'yyyy-MM-dd', new Date());
  dateObject.setHours(0, 0, 0, 0);
  return format(dateObject, 'yyyy-MM-dd');
};
export const determineScheduleDataCacheSeconds = (today: Date, travelDate: Date) => {
  // If the travel date is more than two weeks in future, cache it for longer
  if (travelDate.getTime() - today.getTime() > 14 * 24 * 3600 * 1000) {
    return 6 * 3600; // 6 hours
  }
  let sameDayCacheSeconds = 60 * 60; // Default to 1 hour for same day

  /* Commenting this out, as I don't think it's needed after all, since the date string will
   * always be part of the TDS request, and therefore part of the cache key
  // But don't go past midnight
  const midnight = travelDate.clone().endOf("day");
  const secondsLeftInDay = midnight.diff(travelDate, 'seconds');
  if (secondsLeftInDay < sameDayCacheSeconds) {
    sameDayCacheSeconds = secondsLeftInDay;
  }
  */
  return sameDayCacheSeconds;
};

/*
function distanceBetweenTwoLatLongs(lat1 : number, lon1 : number, lat2 : number, lon2 : number) {
  const φ1 = lat1 * Math.PI/180, φ2 = lat2 * Math.PI/180, Δλ = (lon2-lon1) * Math.PI/180, R = 3958.8; // miles
  const d = Math.acos( Math.sin(φ1)*Math.sin(φ2) + Math.cos(φ1)*Math.cos(φ2) * Math.cos(Δλ) ) * R;
}
*/

/* Filter out trips based on carrier logic.  Return true to have it filtered out */
export const filterSchedulesByCarrierIds = (
  cityRoute: StrapiCityRouteDatum,
  trueNumber: string,
  allSchedules: ScheduleProduct[],
  departTime: string,
  trailwaysOnly?: boolean
) => {
  /*
  if (!cityRoute || !cityRoute.attributes) {
    return false;
  }
  */
  // ADT 3xx routes are an artifact of a weird TDS thing.  Remove them.
  if (trueNumber.match(/(ADT|NYT) 3\d{2}/)) {
    return true;
  }
  const parts = trueNumber.split(' - ');
  const trailwaysCarriers = ['ADT', 'BTW', 'FAB', 'NYT', 'PHK'];
  if (trailwaysOnly && !trailwaysCarriers.includes(parts[0].split(' ')[0])) {
    return true;
  }
  if (
    cityRoute &&
    cityRoute.attributes &&
    parts.length === 1 &&
    cityRoute.attributes?.carrier_direct_filter &&
    cityRoute.attributes?.carrier_direct_filter.data.length
  ) {
    const directFilterCarrierIds = cityRoute.attributes.carrier_direct_filter.data.map(
      (item) => item.attributes.carrier_id
    );
    const currentCarrierId = parts[0].split(' ')[0];

    if (directFilterCarrierIds.includes(currentCarrierId)) {
      const currentDepartureTime = new Date(departTime).getTime();

      // Find if there's a Trailways trip within 60 minutes of this trip
      const trailwaysTrip = allSchedules.find((schedule) => {
        const trueNumberParts = schedule.scheduleRun.trueNumber.split(' - ');
        if (trueNumberParts.length !== 1) {
          return false;
        }
        if (!trailwaysCarriers.includes(trueNumberParts[0].split(' ')[0])) {
          return false;
        }
        const trailwaysDepartureTime = new Date(schedule.scheduleRun.departTime).getTime();

        return Math.abs(trailwaysDepartureTime - currentDepartureTime) <= 2 * 60 * 60 * 1000;
      });

      // If there's a Trailways trip within 60 minutes, suppress this carrier's trip
      if (trailwaysTrip) {
        return true;
      }
    }
  }

  const allCarriersCodes = parts.map((carrierString) => {
    return carrierString.split(' ')[0];
  });
  const hasGreyhound = allCarriersCodes.some((carrierCode) => carrierCode === 'GLI' || carrierCode === 'FLX');
  if (hasGreyhound) {
    const currentDepartureTime = new Date(departTime).getTime();

    // Find if there's a Peter Pan trip within 60 minutes of this trip
    const peterPanTrip = allSchedules.find((schedule) => {
      const trueNumberParts = schedule.scheduleRun.trueNumber.split(' - ');
      const allOtherCarriersCodes = trueNumberParts.map((carrierString) => {
        return carrierString.split(' ')[0];
      });
      // If there's no Peter Pan or Bonanza trip, don't suppress
      if (!allOtherCarriersCodes.some((carrierCode) => carrierCode === 'PPB' || carrierCode == 'BZ')) {
        return false;
      }
      const peterPanDepartureTime = new Date(schedule.scheduleRun.departTime).getTime();
      // Suppress if within 60 minutes
      return Math.abs(peterPanDepartureTime - currentDepartureTime) <= 2 * 60 * 60 * 1000;
    });

    // If there's a Peter Pan trip within 60 minutes, suppress this carrier's trip
    if (peterPanTrip) {
      return true;
    }
  }
  if (
    cityRoute &&
    cityRoute.attributes &&
    cityRoute.attributes.carrier_interline_filter &&
    cityRoute.attributes.carrier_interline_filter.data.length
  ) {
    const carrierIds = cityRoute.attributes.carrier_interline_filter.data.map((item) => item.attributes.carrier_id);
    if (carrierIds.find((id) => trueNumber.includes(id))) {
      return true;
    }
  }
  return false;
};

export const isCachedTripPastDepartureTime = (departTime: string) => {
  const currentTime = new Date();
  const departureTime = new Date(departTime);

  // Convert both times to UTC in milliseconds
  const currentTimeUTC = currentTime.getTime();
  const departureTimeUTC = departureTime.getTime();

  const timeDifferenceInMinutes = (currentTimeUTC - departureTimeUTC) / (1000 * 60);

  return timeDifferenceInMinutes > 5;
};

// Check if a YYYY-MM-DD date string is in the past
export const isValidTravelDate = (travelDate: string, timeZone?: string) => {
  return true;
  /*  Disabling for now, as it's not working properly
  if (!timeZone) {
    timeZone = 'America/New_York';
  }
  const now = new Date();
  return travelDate >= format(now, 'yyyy-MM-dd', { in: tz(timeZone) });
  */
};
